class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.onOverlayClick = this.close.bind(this);
    this.bindOverlayClick();
    this.setHeaderCartIconAccessibility();
    this.cartUpdateUnsubscriber = undefined;
    this.isRefreshing = false;
  }

  bindOverlayClick() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay) return;
    overlay.removeEventListener('click', this.onOverlayClick);
    overlay.addEventListener('click', this.onOverlayClick);
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-drawer') return;
      this.refreshCart();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy, { refresh = true } = {}) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (refresh) this.refreshCart();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open(undefined, { refresh: false });
    });
  }

  async refreshCart({ openDrawer = false } = {}) {
    if (this.isRefreshing) return;
    this.isRefreshing = true;

    try {
      const sectionsToRender = this.getSectionsToRender();
      const sectionIds = sectionsToRender.map((section) => section.id).join(',');
      const [sectionsResponse, cartResponse] = await Promise.all([
        fetch(`${routes.cart_url}?sections=${sectionIds}`),
        fetch(`${routes.cart_url}.js`),
      ]);
      const sectionsData = await sectionsResponse.json();
      const cartData = await cartResponse.json();
      const sections = sectionsData.sections || sectionsData;

      sectionsToRender.forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement || !sections[section.id]) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
      });

      this.bindOverlayClick();

      this.classList.toggle('is-empty', cartData.item_count === 0);
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner) drawerInner.classList.toggle('is-empty', cartData.item_count === 0);

      if (openDrawer) this.open(undefined, { refresh: false });
    } catch (error) {
      console.error(error);
    } finally {
      this.isRefreshing = false;
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

window.addEventListener('pageshow', async (event) => {
  const navEntry = performance.getEntriesByType('navigation')[0];
  const isBackForward = event.persisted || (navEntry && navEntry.type === 'back_forward');

  if (!isBackForward) return;

  customElements.whenDefined('cart-drawer').then(() => {
    const cartDrawer = document.querySelector('cart-drawer');
    cartDrawer?.refreshCart();
  });
});
